/* MissionCreateModal — configuratie-modal voor een nieuwe hunt-mission.
 * Ondersteunt 3 mission-types (growth signals / vacancy burst / reverse hunt),
 * multi-select data-sources en schedule-keuze.
 * Geëxtraheerd uit Signals.jsx (refactor).
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { Target, X, Loader2, Sparkles, Info } from "lucide-react";

export function MissionCreateModal({ workspace, onClose, onCreated }) {
    const { t } = useTranslation();
    // Nooit standaard synthetisch: kies de eerste actieve échte databron.
    // 'llm' is alleen nog een expliciete, zichtbare keuze van de gebruiker.
    const REAL_PRIORITY = ["apollo", "apollo_mcp", "serper", "perplexity", "apify_leads", "linkedin_jobs", "apify_jobs", "google_maps", "zevari", "scaliq"];
    const firstRealSource = REAL_PRIORITY.find((s) => workspace?.data_sources?.[s]) || null;
    const [form, setForm] = useState({
        title: "", prompt: "",
        target_role: "", target_sector: "", target_country: "BE",
        target_sizes: ["51-200"],
        schedule: "daily", data_sources: [firstRealSource || "llm"], run_now: true,
        mission_type: "growth_signals",
        min_open_vacancies: 10,
        focus_input: "",
        source_candidate_id: "",
        notify_slack: false,
        notify_email: "",
    });
    const [saving, setSaving] = useState(false);
    const [cronosCandidates, setCronosCandidates] = useState([]);
    const sources = workspace?.data_sources || { llm: true, apollo: false, perplexity: false };
    const isVacancyBurst = form.mission_type === "vacancy_burst";
    const isReverse = form.mission_type === "candidate_to_vacancies";
    const selectedCandidate = cronosCandidates.find((c) => c.id === form.source_candidate_id);

    // Lazy-load Cronos candidates when the user picks the reverse mission type
    useEffect(() => {
        if (isReverse && cronosCandidates.length === 0) {
            api.get("/talent-pool", { params: { pool: "cronos", limit: 300 } })
                .then((r) => setCronosCandidates(r.data || []))
                .catch(() => setCronosCandidates([]));
        }
    }, [isReverse, cronosCandidates.length]);

    // Enforce mission-type source requirements
    const VACANCY_OK = ["perplexity", "serper", "apify_jobs", "linkedin_jobs"];
    useEffect(() => {
        if (isReverse) {
            // Reverse hunts: force perplexity (backend vereist perplexity/serper)
            setForm((f) => f.data_sources.includes("perplexity") ? f : { ...f, data_sources: [...f.data_sources.filter((s) => s !== "llm"), "perplexity"].slice(-3) });
        } else if (isVacancyBurst) {
            // Vacancy burst: minstens één live jobs-bron (perplexity/serper/apify_jobs/linkedin_jobs)
            setForm((f) => f.data_sources.some((s) => VACANCY_OK.includes(s)) ? f : { ...f, data_sources: [...f.data_sources.filter((s) => s !== "llm"), "perplexity"].slice(-3) });
        }
    }, [isVacancyBurst, isReverse]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-derived title for reverse hunts — user does NOT have to invent one.
    const autoTitle = isReverse && selectedCandidate
        ? `${selectedCandidate.name} → ${form.target_role?.trim() || "vacatures"}`
        : "";

    const toggleSize = (sz) => {
        setForm((f) => ({
            ...f,
            target_sizes: f.target_sizes.includes(sz)
                ? f.target_sizes.filter((s) => s !== sz)
                : [...f.target_sizes, sz],
        }));
    };
    const toggleSource = (src) => {
        setForm((f) => {
            const has = f.data_sources.includes(src);
            let next = has ? f.data_sources.filter((s) => s !== src) : [...f.data_sources, src];
            if (next.length === 0) next = [firstRealSource || "llm"]; // never allow empty — echte bron eerst
            // Reverse must keep perplexity; vacancy burst needs ≥1 live jobs-bron
            if (isReverse && !next.includes("perplexity")) next = [...next, "perplexity"];
            if (isVacancyBurst && !next.some((s) => VACANCY_OK.includes(s))) next = [...next, "perplexity"];
            return { ...f, data_sources: next };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        // Reverse hunts derive their title automatically — for other types the
        // user must have typed one.
        if (!isReverse && !form.title.trim()) { toast.error(i18n.t("missions.title_required")); return; }
        if (isReverse && !form.source_candidate_id) {
            toast.error(i18n.t("missions.pick_candidate"));
            return;
        }
        // For growth_signals the "Sales info voor Agent" textarea IS the prompt
        // — required. For vacancy_burst and reverse hunts we auto-derive the
        // prompt from the other fields (title, focus_input, candidate) so the
        // user never has to type one.
        let effectivePrompt = (form.prompt || "").trim();
        if (!isVacancyBurst && !isReverse && !effectivePrompt) {
            toast.error(i18n.t("missions.info_required"));
            return;
        }
        if (isVacancyBurst && !effectivePrompt) {
            const bits = [
                `Vind bedrijven in ${form.target_country || "BE"}`,
                form.target_sizes.length ? `(grootte ${form.target_sizes.join(", ")})` : "",
                `met minstens ${form.min_open_vacancies || 10} open vacatures`,
                form.focus_input ? `passend bij deze context:\n\n${form.focus_input.trim()}` : "",
            ].filter(Boolean);
            effectivePrompt = bits.join(" ").trim();
        }
        if (isReverse && !effectivePrompt) {
            const cand = cronosCandidates.find((c) => c.id === form.source_candidate_id);
            const parts = [
                `Vind bedrijven in ${form.target_country || "BE"}`,
                form.target_sizes.length ? `(grootte ${form.target_sizes.join(", ")})` : "",
                `met open vacatures die passen bij kandidaat ${cand?.name || ""}`,
                cand?.title ? `(${cand.title})` : "",
                cand?.location ? `— locatie ${cand.location}` : "",
                form.focus_input ? `\n\nExtra context van operator:\n${form.focus_input.trim()}` : "",
            ].filter(Boolean);
            effectivePrompt = parts.join(" ").trim() || "Match kandidaat naar bedrijven met open vacatures.";
        }
        const payload = {
            ...form,
            prompt: effectivePrompt,
            title: isReverse ? autoTitle : form.title.trim(),
            // legacy singular fields for older callers/consumers
            target_size: form.target_sizes[0] || null,
            data_source: form.data_sources[0] || firstRealSource || "llm",
        };
        setSaving(true);
        try {
            const r = await api.post("/hunt-missions", payload);
            onCreated?.(r.data.mission);
        } catch (err) { toast.error(err?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setSaving(false); }
    };

    const SOURCES = [
        { id: "llm",        label: "LLM (Claude) — synthetisch",   note: i18n.t("missions.src_llm") },
        { id: "apollo",     label: "Apollo.io",      note: i18n.t("missions.src_apollo") },
        { id: "apollo_mcp", label: "Apollo MCP (OAuth)", note: i18n.t("missions.src_apollo_mcp") },
        { id: "perplexity", label: "Perplexity Sonar Pro", note: i18n.t("missions.src_perplexity") },
        { id: "serper",     label: "Serper (Google)",     note: i18n.t("missions.src_serper") },
        { id: "linkedin_jobs", label: "LinkedIn Jobs (Bright Data)", note: i18n.t("missions.src_linkedin_jobs") },
        { id: "apify_leads", label: "Apify Leads Finder", note: i18n.t("missions.src_apify_leads") },
        { id: "apify_jobs", label: "LinkedIn Jobs (Apify)", note: i18n.t("missions.src_apify_jobs") },
        { id: "google_maps", label: "Google Maps (Apify)", note: i18n.t("missions.src_google_maps") },
        { id: "zevari",     label: "Zevari (LinkedIn Signal-scan)", note: i18n.t("missions.src_zevari") },
        { id: "scaliq",     label: "ScalIQ (LinkedIn People-search)", note: i18n.t("missions.src_scaliq") },
    ];

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}>
            <form onSubmit={submit} data-testid="mission-create-modal"
                className="w-full max-w-2xl bg-[var(--jr-surface)] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="font-display text-xl tracking-tight inline-flex items-center gap-2">
                            <Target className="text-[var(--jr-brand)]" size={20} /> {i18n.t("missions.new_title")}
                        </h3>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">{i18n.t("missions.new_sub")}</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-[var(--jr-text-3)] hover:text-white"><X size={18} /></button>
                </div>

                <div className="space-y-3">
                    {/* Mission Type toggle — Growth Signals · Vacancy Burst · Candidate→Vacancies */}
                    <FieldX label={t("signals.new_mission")} testid="m-mission-type">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {[
                                { id: "growth_signals", label: t("signals.mission_type_growth_signals"),
                                  note: t("signals.mission_type_growth_signals_note") },
                                { id: "vacancy_burst", label: t("signals.mission_type_vacancy_burst"),
                                  note: t("signals.mission_type_vacancy_burst_note") },
                                { id: "candidate_to_vacancies", label: t("signals.mission_type_reverse"),
                                  note: t("signals.mission_type_reverse_note") },
                            ].map((mt) => {
                                const active = form.mission_type === mt.id;
                                return (
                                    <button type="button" key={mt.id} onClick={() => setForm({ ...form, mission_type: mt.id })}
                                        data-testid={`m-mtype-${mt.id}`}
                                        className={`text-left rounded-lg border-2 p-3 transition-all ${active
                                            ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/15 shadow-[0_0_0_3px_rgba(255,140,42,0.08)]"
                                            : "border-white/8 bg-[var(--jr-surface-2)] hover:border-white/15"}`}>
                                        <div className="text-sm font-medium text-white">{mt.label}</div>
                                        <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5">{mt.note}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </FieldX>

                    {/* Reverse-hunt candidate picker */}
                    {isReverse && (
                        <div className="rounded-lg border border-[#3b82f6]/30 bg-[#3b82f6]/[0.05] p-3 space-y-2" data-testid="reverse-hunt-fields">
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#3b82f6]">
                                {t("signals.reverse_pick_candidate")}
                            </div>
                            {cronosCandidates.length === 0 ? (
                                <div className="text-xs text-[var(--jr-text-3)]">
                                    {t("signals.reverse_no_candidates")}
                                </div>
                            ) : (
                                <select value={form.source_candidate_id}
                                    onChange={(e) => setForm({ ...form, source_candidate_id: e.target.value })}
                                    data-testid="m-source-candidate"
                                    className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#3b82f6]">
                                    <option value="">— {t("signals.reverse_choose_first")} —</option>
                                    {cronosCandidates.map((c) => (
                                        <option key={c.id} value={c.id}>{`${c.name} · ${c.title} · ${c.location}`}</option>
                                    ))}
                                </select>
                            )}
                            <p className="text-[10px] text-[var(--jr-text-3)]">
                                {t("signals.reverse_hint")}
                            </p>
                        </div>
                    )}

                    {/* Titel first — auto for reverse hunts */}
                    {isReverse ? (
                        <div className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2.5" data-testid="m-title-auto">
                            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{i18n.t("missions.title_auto")}</div>
                            <div className="text-sm text-white mt-0.5">
                                {autoTitle || <span className="text-[var(--jr-text-3)] italic">{i18n.t("missions.pick_first")}</span>}
                            </div>
                        </div>
                    ) : (
                        <FieldX label={i18n.t("missions.lbl_title")} testid="m-title">
                            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder={isVacancyBurst ? i18n.t("missions.ph_title_vac") : i18n.t("missions.ph_title_growth")}
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                        </FieldX>
                    )}

                    {/* Vacancy Burst extra: alleen aantal vacatures */}
                    {isVacancyBurst && (
                        <FieldX label={i18n.t("missions.lbl_min_vac")} testid="m-min-vac">
                            <input type="number" min={1} max={200} value={form.min_open_vacancies}
                                onChange={(e) => setForm({ ...form, min_open_vacancies: parseInt(e.target.value) || 10 })}
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                            <p className="mt-1 text-[10px] text-[var(--jr-text-3)]">
                                <Sparkles size={9} className="inline text-[var(--jr-brand)]"/> {i18n.t("missions.min_vac_hint")}
                            </p>
                        </FieldX>
                    )}

                    {/* Sales info voor Agent — hier plak je JD/CV/context. Verplicht
                        voor growth_signals, optioneel voor Vacancy Burst én Reverse Hunt. */}
                    <FieldX
                        label={isVacancyBurst || isReverse ? i18n.t("missions.lbl_info_opt") : i18n.t("missions.lbl_info_req")}
                        testid={isVacancyBurst || isReverse ? "m-focus-input" : "m-prompt"}>
                        <textarea
                            required={!isVacancyBurst && !isReverse}
                            rows={5}
                            value={isVacancyBurst || isReverse ? form.focus_input : form.prompt}
                            onChange={(e) => (isVacancyBurst || isReverse)
                                ? setForm({ ...form, focus_input: e.target.value })
                                : setForm({ ...form, prompt: e.target.value })}
                            placeholder={isReverse
                                ? i18n.t("missions.ph_info_reverse")
                                : isVacancyBurst
                                    ? i18n.t("missions.ph_info_vac")
                                    : i18n.t("missions.ph_info_growth")}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)] leading-relaxed" />
                        <p className="mt-1 text-[10px] text-[var(--jr-text-3)] leading-relaxed">
                            <Info size={9} className="inline text-[var(--jr-info)]"/> {i18n.t("missions.info_hint")}
                        </p>
                    </FieldX>

                    {/* Regio + Grootte only */}
                    <div className="grid grid-cols-2 gap-3">
                        <FieldX label={i18n.t("missions.lbl_region")} testid="m-country">
                            <select value={form.target_country} onChange={(e) => setForm({ ...form, target_country: e.target.value })}
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                                <option value="BE">{i18n.t("missions.country_be")}</option><option value="NL">{i18n.t("missions.country_nl")}</option><option value="BE/NL">BE + NL</option>
                                <option value="EU">{i18n.t("missions.country_eu")}</option><option value="DACH">DACH</option><option value="UK">{i18n.t("missions.country_uk")}</option>
                            </select>
                        </FieldX>
                        <FieldX label={`${i18n.t("missions.lbl_size")} ${form.target_sizes.length > 0 ? `(${form.target_sizes.length})` : ""}`} testid="m-size">
                            <div className="flex flex-wrap gap-1.5" data-testid="m-size-chips">
                                {["1-10","11-50","51-200","201-500","501-1000","1000+"].map((sz) => {
                                    const active = form.target_sizes.includes(sz);
                                    return (
                                        <button type="button" key={sz} onClick={() => toggleSize(sz)}
                                            data-testid={`m-size-${sz}`}
                                            className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${active
                                                ? "bg-[var(--jr-brand)] text-black border-[var(--jr-brand)] font-medium"
                                                : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                                            {sz}
                                        </button>
                                    );
                                })}
                            </div>
                        </FieldX>
                    </div>

                    <FieldX label="Schedule" testid="m-schedule">
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { id: "manual", label: i18n.t("missions.sched_manual"), note: i18n.t("missions.sched_manual_note") },
                                { id: "daily",  label: i18n.t("missions.sched_daily"), note: i18n.t("missions.sched_daily_note") },
                                { id: "weekly", label: i18n.t("missions.sched_weekly"), note: i18n.t("missions.sched_weekly_note") },
                            ].map((s) => {
                                const active = form.schedule === s.id;
                                return (
                                    <button type="button" key={s.id} onClick={() => setForm({ ...form, schedule: s.id })} data-testid={`m-schedule-${s.id}`}
                                        className={`text-left rounded-lg border-2 p-3 transition-all ${active ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/15 shadow-[0_0_0_3px_rgba(255,140,42,0.08)]" : "border-white/8 bg-[var(--jr-surface-2)] hover:border-white/15"}`}>
                                        <div className="text-sm font-medium text-white">{s.label}</div>
                                        <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5">{s.note}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </FieldX>

                    {/* Datasource — multi-select. Reverse-hunts require Perplexity; Vacancy Burst vereist ≥1 live jobs-bron. */}
                    <FieldX label={`Datasource — kies één of meerdere${isReverse ? " (Perplexity vereist)" : isVacancyBurst ? " (live jobs-bron vereist)" : ""}`} testid="m-source">
                        <div className="grid sm:grid-cols-3 gap-2">
                            {SOURCES.map((s) => {
                                const enabled = sources[s.id];
                                const active = form.data_sources.includes(s.id);
                                const locked = (isReverse && s.id === "perplexity") ||
                                    (isVacancyBurst && s.id === "perplexity" &&
                                     !form.data_sources.some((x) => x !== "perplexity" && VACANCY_OK.includes(x)));
                                return (
                                    <button type="button" key={s.id}
                                        data-testid={`hm-src-${s.id}`}
                                        onClick={() => !locked && enabled && toggleSource(s.id)}
                                        disabled={!enabled || locked}
                                        data-testid={`m-source-${s.id}`}
                                        title={locked ? i18n.t("missions.pplx_required") : undefined}
                                        className={`text-left rounded-lg border-2 p-3 transition-all relative ${active && enabled ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/15 shadow-[0_0_0_3px_rgba(255,140,42,0.08)]" : !enabled ? "border-white/5 bg-[var(--jr-surface-2)]/40 opacity-50 cursor-not-allowed" : "border-white/8 bg-[var(--jr-surface-2)] hover:border-white/15"}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="text-sm font-medium text-white">{s.label}</div>
                                            {active && enabled && (
                                                <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[var(--jr-brand)]/25 text-[var(--jr-brand)]">✓</span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-[var(--jr-text-3)] mt-1 leading-snug">{s.note}</div>
                                        {!enabled && <div className="mt-1 text-[10px] text-[var(--jr-amber)]">{i18n.t("missions.src_off")}</div>}
                                        {locked && <div className="mt-1 text-[10px] text-[var(--jr-amber)]">{i18n.t("missions.src_locked")}</div>}
                                    </button>
                                );
                            })}
                        </div>
                    </FieldX>

                    {/* Externe notificaties — Slack webhook (workspace) + e-mail per missie */}
                    <FieldX label={t("missions.notify_label")} testid="m-notify">
                        <div className="space-y-2">
                            <label className="flex items-start gap-2 text-xs text-[var(--jr-text-2)] cursor-pointer">
                                <input type="checkbox" checked={form.notify_slack}
                                    onChange={(e) => setForm({ ...form, notify_slack: e.target.checked })}
                                    data-testid="m-notify-slack" className="mt-0.5" />
                                <span>
                                    <span className="text-white">{t("missions.notify_slack")}</span>
                                    <span className="block text-[10px] text-[var(--jr-text-3)] mt-0.5">{t("missions.notify_slack_note")}</span>
                                </span>
                            </label>
                            <input value={form.notify_email}
                                onChange={(e) => setForm({ ...form, notify_email: e.target.value })}
                                placeholder={t("missions.notify_email_ph")}
                                type="email"
                                data-testid="m-notify-email"
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[var(--jr-brand)]" />
                        </div>
                    </FieldX>

                    <label className="inline-flex items-center gap-2 text-xs text-[var(--jr-text-2)]">
                        <input type="checkbox" checked={form.run_now} onChange={(e) => setForm({ ...form, run_now: e.target.checked })} data-testid="m-runnow" />
                        {i18n.t("missions.run_now_label")}
                    </label>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={saving}
                        className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                    <button type="submit" disabled={saving} data-testid="m-submit"
                        className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Start mission
                    </button>
                </div>
            </form>
        </div>
    );
}

function FieldX({ label, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1 block">{label}</span>
            {children}
        </label>
    );
}
